import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/ui/Logo";
import { ConnectFooter } from "@/components/connect/ConnectFooter";
import { ConnectPortalLayout } from "@/components/layout/ConnectPortalLayout";
import { DialerLanguageToggle } from "@/components/connect/DialerLanguageToggle";
import { CountryDetailContent } from "@/components/connect/CountryDetailContent";
import { useCountryRates } from "@/hooks/useCountryRates";
import { getFlag } from "@/lib/country-flags";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { SEOHead } from "@/components/seo/SEOHead";
import { getCanonicalUrl, getBreadcrumbSchema } from "@/config/seo";
import {
  getCountrySlugByDialCode,
  getCountrySlugByName,
  generateSlugFromName,
} from "@/config/connect-countries";

interface GenericCountryPageProps {
  slug: string;
}

function GenericContent({ slug, isAuthenticated }: { slug: string; isAuthenticated: boolean }) {
  const { t } = useTranslation("common");
  const { all } = useCountryRates();

  // Find country group by slug - match across landline + mobile entries.
  // Use the same `generateSlugFromName` helper that ConnectRatesPage uses
  // when building outbound links, so slugs stay symmetric (e.g. names
  // containing parens/commas still resolve).
  const group = useMemo(() => {
    const slugLower = slug.toLowerCase();
    const matching = all.filter((c) => generateSlugFromName(c.countryName) === slugLower);
    if (matching.length === 0) return null;

    const landline = matching.find((m) => m.destinationType !== "mobile");
    const mobile = matching.find((m) => m.destinationType === "mobile");
    const primary = landline ?? matching[0];

    return {
      countryName: primary.countryName,
      countryCode: primary.countryCode,
      flag: primary.flag,
      landlineCents: landline?.ratePerMinuteCents ?? primary.ratePerMinuteCents,
      mobileCents: mobile?.ratePerMinuteCents ?? null,
    };
  }, [all, slug]);

  if (!group) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <h1 className="text-3xl font-bold mb-4">{t("noCountriesFound")}</h1>
        <Link to="/connect-portal/rates">
          <Button>{t("viewRates")}</Button>
        </Link>
      </div>
    );
  }

  const flagEmoji = getFlag({
    dialCode: group.countryCode,
    countryName: group.countryName,
    flagEmoji: group.flag,
  });

  // Try to resolve a configured slug for nearby destinations bar
  const configuredSlug =
    getCountrySlugByDialCode(group.countryCode) ||
    getCountrySlugByName(group.countryName) ||
    undefined;

  return (
    <CountryDetailContent
      slug={configuredSlug}
      countryName={group.countryName}
      dialCode={group.countryCode}
      ratePerMinuteCents={group.landlineCents}
      mobileRateCents={group.mobileCents ?? undefined}
      flagEmoji={flagEmoji}
      isAuthenticated={isAuthenticated}
    />
  );
}

export function GenericCountryPage({ slug }: GenericCountryPageProps) {
  const { t } = useTranslation("common");
  const { user, isLoading } = useAuth();
  const isAuthenticated = !!user;

  if (isLoading) {
    return <div className="min-h-[100dvh] bg-background" />;
  }

  if (isAuthenticated) {
    return (
      <ConnectPortalLayout requireAuth={false}>
        <GenericContent slug={slug} isAuthenticated={true} />
      </ConnectPortalLayout>
    );
  }

  const displayName = slug.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={`Call ${displayName} | elefa Connect`}
        description={`Affordable HD calls to ${displayName}. Credit never expires. No contracts. No lock-in.`}
        canonicalUrl={getCanonicalUrl(`/connect-portal/${slug}`)}
        structuredData={getBreadcrumbSchema([
          { name: 'Home', url: 'https://elefa.tel/' },
          { name: displayName, url: `https://elefa.tel/connect-portal/${slug}` },
        ])}
      />
      <nav className="container py-6 flex items-center justify-between">
        <Link to="/connect-portal" className="flex items-center gap-2">
          <Logo variant="header" size="md" asLink={false} withIcon fullName />
        </Link>
        <div className="flex items-center gap-3">
          <DialerLanguageToggle />
          <Link to="/connect-portal/auth">
            <Button variant="outline" size="sm">{t("getStarted")}</Button>
          </Link>
        </div>
      </nav>
      <GenericContent slug={slug} isAuthenticated={false} />
      <ConnectFooter />
    </div>
  );
}
